// pages/TravelExpense/index.js
//
// Legacy menu: "Travel Expense Approval"
// Backend:
//   GET /api/TravelExpense/my-requests
//   GET /api/TravelExpense/awaiting-approval     (manager-only)
//
// The page has two tabs:
//   "My Trips"          — caller's own requests (always visible)
//   "Awaiting Approval" — trips needing the caller's vote (manager-gated)

import React, { useEffect, useMemo, useState } from "react";
import { ColumnFilterRow, applyColumnFilters } from "../../Components/Common/DashboardKit";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import { Container, Card, CardBody, Input, InputGroup, Badge, Alert, Button } from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
    DashboardPageHeader, EmptyState, StandardTableToolbar, ResponsiveTabs, TableSkeleton } from "../../Components/Common/DashboardKit";
import api from "../../api";
import useAuth from "../Personal Attendance/useAuth";

const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString(); } catch { return "—"; } };
const fmtMoney = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    if (!isFinite(n)) return "—";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};

// ExpenseApproval.Status legacy codes (TravelExpenseController):
//   1 Draft  2 Submitted  3 Approved  4 Denied
const StatusBadge = ({ status }) => {
    const map = { 1: ["Draft", "secondary"], 2: ["Submitted", "warning"], 3: ["Approved", "success"], 4: ["Denied", "danger"] };
    const [label, color] = map[status] || [`Status ${status}`, "info"];
    return <Badge color={color} pill style={{ fontSize: 11 }}>{label}</Badge>;
};

const TravelExpenseIndex = () => {
    const navigate = useNavigate();
    const auth = useAuth();
    const canApprove = auth.isManager || auth.isItAdmin || auth.isSuperUser;

    const TABS = useMemo(() => [
        { key: "my", label: "My Trips" },
        ...(canApprove ? [{ key: "awaiting", label: "Awaiting My Approval" }] : []),
    ], [canApprove]);

    const [activeTab, setActiveTab] = useState("my");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");

    useEffect(() => {
        let alive = true;
        setLoading(true); setError(null);
        const url = activeTab === "my" ? "TravelExpense/my-requests" : "TravelExpense/awaiting-approval";
        api.get(url)
            .then(({ data }) => {
                if (!alive) return;
                setItems(Array.isArray(data?.Items) ? data.Items : []);
            })
            .catch((e) => { if (alive) setError(e?.response?.data?.error || e?.message || "Failed to load"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [activeTab]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((r) =>
            String(r.nameOfTrip ?? "").toLowerCase().includes(q)
            || String(r.primaryObjective ?? "").toLowerCase().includes(q)
            || String(r.requesterName ?? "").toLowerCase().includes(q)
        );
    }, [items, search]);

    // Round 36 — EPO-style per-column filters + click-to-sort + CSV.
    const [columnFilters, setColumnFilters] = useState({});
    const tblColumns = useMemo(() => [
        { id: "id",        label: "ID",   type: "text", get: (r) => String(r.id ?? "") },
        { id: "nameOfTrip",label: "Trip", type: "text", get: (r) => r.nameOfTrip || "" },
        { id: "primaryObjective", label: "Primary Objective", type: "text", get: (r) => r.primaryObjective || "" },
        ...(activeTab !== "my" ? [{ id: "requesterName", label: "Requester", type: "text", get: (r) => r.requesterName || "" }] : []),
        { id: "leaveHomeDate",  label: "Leave",  type: "date", get: (r) => r.leaveHomeDate || "" },
        { id: "returnHomeDate", label: "Return", type: "date", get: (r) => r.returnHomeDate || "" },
        { id: "status", label: "Status", type: "text", get: (r) => r.status || "" },
        { id: "total",  label: "Estimated Total", type: "text", get: (r) => String(r.total ?? "") },
        { id: "act", label: "", type: "none" },
    ], [activeTab]);
    const filteredBase = applyColumnFilters(filtered, tblColumns, columnFilters);
    const { sorted: view, sort, toggleSort } = useTableSort(filteredBase, tblColumns);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title="Travel Expense Approval"
                    breadcrumb={[{ label: "Travel Expense Approval" }]}
                    subtitle={activeTab === "my"
                        ? `Your travel requests (${items.length})`
                        : `Trips awaiting your approval (${items.length})`}
                    breadcrumbItems={[{ label: "Travel Expense" }]}
                    actions={
                        <Button color="primary" onClick={() => navigate("/travel-expense/new")}>
                            + New Request
                        </Button>
                    }
                />
                {error && <Alert color="danger" className="mt-2">{error}</Alert>}
                <ResponsiveTabs tabs={TABS} activeKey={activeTab} onChange={setActiveTab} />  {/* R40: items → tabs */}
                <Card className="mt-3">
                    <CardBody>
                        <StandardTableToolbar right={
                                {/* Round 36 — search replaced by the per-column filter row */}
                        } />
                        {loading ? (
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <tbody><TableSkeleton rows={6} columns={10} /></tbody>
                                </table>
                            </div>
                        ) : view.length === 0 ? (
                            <EmptyState
                                title={activeTab === "my" ? "No travel requests yet" : "Nothing waiting on you"}
                                description={activeTab === "my"
                                    ? "Your submitted trips will appear here."
                                    : "Either there's nothing to approve, or you've already voted."}
                            />
                        ) : (
                            <div className="table-responsive">
                                {/* Round 36 — CSV of the filtered + sorted list */}
                                <div className="d-flex justify-content-end mb-2">
                                    <button type="button" className="btn btn-soft-primary btn-sm"
                                        onClick={() => exportRowsToCsv("travel-requests.csv", tblColumns.filter((c) => c.type !== "none"), view)}>
                                        <i className="ri-download-2-line align-bottom me-1" />Export CSV ({view.length})
                                    </button>
                                </div>
                                <table className="table table-striped table-bordered mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            {tblColumns.map((c) => (
                                                <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />
                                            ))}
                                        </tr>
                                        <ColumnFilterRow columns={tblColumns} filters={columnFilters} onChange={setColumnFilters} />
                                    </thead>
                                    <tbody>
                                        {view.map((r) => (
                                            <tr key={r.id}>
                                                <td>{r.id}</td>
                                                <td style={{ maxWidth: 260 }} className="text-truncate" title={r.nameOfTrip ?? ""}>{r.nameOfTrip ?? "—"}</td>
                                                <td style={{ maxWidth: 260 }} className="text-truncate" title={r.primaryObjective ?? ""}>{r.primaryObjective ?? "—"}</td>
                                                {activeTab !== "my" && <td>{r.requesterName ?? r.empid}</td>}
                                                <td>{fmtDate(r.leaveHomeDate)}</td>
                                                <td>{fmtDate(r.returnHomeDate)}</td>
                                                <td><StatusBadge status={r.status} /></td>
                                                <td className="text-end">{fmtMoney(r.total)}</td>
                                                <td>
                                                    <button type="button" className="btn btn-link btn-sm p-0"
                                                        onClick={() => navigate(`/travel-expense/${r.id}`)}>
                                                        {activeTab === "my" ? "View" : "Review"}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default TravelExpenseIndex;
